import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { Copy, Download, Eye, Code, Maximize2, RefreshCw, GripVertical, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLabelStore } from '@/stores/labelStore'
import { useElementsStore } from '@/stores/elementsStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { generateZPL } from '@/lib/zpl/generator'

const MIN_WIDTH = 250
const MAX_WIDTH = 600
const DEFAULT_WIDTH = 320
const DEBOUNCE_DELAY = 800 // ms delay before refreshing preview

export function ZPLPreviewPanel() {
  const { label } = useLabelStore()
  const { elements } = useElementsStore()
  const { variableData } = useSettingsStore()
  const [copied, setCopied] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [imageKey, setImageKey] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [panelWidth, setPanelWidth] = useState(DEFAULT_WIDTH)
  const [isResizing, setIsResizing] = useState(false)
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null)

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    resizeRef.current = {
      startX: e.clientX,
      startWidth: panelWidth,
    }
  }, [panelWidth])

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeRef.current) return

      // Calculate new width (dragging left increases width, right decreases)
      const delta = resizeRef.current.startX - e.clientX
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeRef.current.startWidth + delta))
      setPanelWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      resizeRef.current = null
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  const zplCode = useMemo(() => {
    return generateZPL(label, elements, variableData)
  }, [label, elements, variableData])

  // Debounced ZPL code for preview - only update after user stops making changes
  const [debouncedZplCode, setDebouncedZplCode] = useState(zplCode)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Fetch preview using POST to avoid URL length limits
  const fetchPreview = useCallback(async (zpl: string, prevUrl: string | null) => {
    if (elements.length === 0) {
      if (prevUrl) {
        URL.revokeObjectURL(prevUrl)
      }
      setPreviewUrl(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setPreviewError(null)

    const dpmm = label.dpi === 203 ? 8 : 12
    const widthInches = (label.width / 25.4).toFixed(3)
    const heightInches = (label.height / 25.4).toFixed(3)
    const apiUrl = `https://api.labelary.com/v1/printers/${dpmm}dpmm/labels/${widthInches}x${heightInches}/0/`

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'image/png',
        },
        body: zpl,
      })

      if (!response.ok) {
        throw new Error(`Labelary API error: ${response.status}`)
      }

      const blob = await response.blob()
      // Revoke previous URL to avoid memory leaks
      if (prevUrl) {
        URL.revokeObjectURL(prevUrl)
      }
      const url = URL.createObjectURL(blob)
      setPreviewUrl(url)
      setPreviewError(null)
    } catch (error) {
      console.error('Preview error:', error)
      setPreviewError(error instanceof Error ? error.message : 'Failed to load preview')
    } finally {
      setIsLoading(false)
    }
  }, [label.dpi, label.width, label.height, elements.length])

  // Debounce ZPL changes to avoid too many API calls
  useEffect(() => {
    // Show loading immediately when ZPL changes
    setIsLoading(true)

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer to update preview after delay
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedZplCode(zplCode)
      setImageKey((k) => k + 1)
    }, DEBOUNCE_DELAY)

    // Cleanup on unmount or when zplCode changes again
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [zplCode])

  // Keep track of previous URL for cleanup
  const prevUrlRef = useRef<string | null>(null)

  // Fetch preview when debounced ZPL changes
  useEffect(() => {
    fetchPreview(debouncedZplCode, prevUrlRef.current)
  }, [debouncedZplCode, imageKey, fetchPreview])

  // Update ref when previewUrl changes
  useEffect(() => {
    prevUrlRef.current = previewUrl
  }, [previewUrl])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current)
      }
    }
  }, [])

  const handleRefresh = () => {
    setImageKey((k) => k + 1)
    setIsLoading(true)
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(zplCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownload = () => {
    const blob = new Blob([zplCode], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${label.name.replace(/\s+/g, '_')}.zpl`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handlePrintPreview = useCallback(() => {
    if (!previewUrl) return

    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    const widthMm = label.width
    const heightMm = label.height

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Label Preview</title>
        <style>
          @page {
            size: auto;
            margin: 10mm;
          }
          body {
            margin: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
          }
          img {
            max-width: 100%;
            height: auto;
            border: 1px solid #ccc;
          }
          .info {
            position: fixed;
            bottom: 10px;
            left: 10px;
            font-family: Arial, sans-serif;
            font-size: 10px;
            color: #666;
          }
          @media print {
            .info { display: none; }
          }
        </style>
      </head>
      <body>
        <img src="${previewUrl}" alt="Label Preview" />
        <div class="info">Label: ${widthMm}mm x ${heightMm}mm @ ${label.dpi} DPI</div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 500);
          };
        <\/script>
      </body>
      </html>
    `)
    printWindow.document.close()
  }, [previewUrl, label])

  return (
    <>
      <div
        className="border-l bg-card flex flex-col relative"
        style={{ width: panelWidth }}
      >
        {/* Resize handle */}
        <div
          className={`absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500/50 transition-colors z-10 ${
            isResizing ? 'bg-blue-500/50' : ''
          }`}
          onMouseDown={handleResizeStart}
        >
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 p-1 rounded bg-muted border opacity-0 hover:opacity-100 transition-opacity">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        <div className="p-3 border-b">
          <h2 className="font-semibold text-sm">ZPL Preview</h2>
        </div>

        <Tabs defaultValue="preview" className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <TabsList className="mx-3 mt-2 grid grid-cols-2">
            <TabsTrigger value="preview">
              <Eye className="h-4 w-4 mr-1" />
              Preview
            </TabsTrigger>
            <TabsTrigger value="code">
              <Code className="h-4 w-4 mr-1" />
              Code
            </TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="flex-1 p-3 pt-2 flex flex-col">
            <div className="flex-1 bg-muted rounded-md flex items-center justify-center relative min-h-[200px]">
              {elements.length > 0 ? (
                <>
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted/80 z-10">
                      <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {previewError ? (
                    <div className="p-3 text-center">
                      <span className="text-sm text-destructive">{previewError}</span>
                    </div>
                  ) : previewUrl ? (
                    <div className="p-3 bg-muted">
                      <img
                        key={imageKey}
                        src={previewUrl}
                        alt="Label Preview"
                        className="max-w-full max-h-full object-contain shadow-md border border-gray-300"
                      />
                    </div>
                  ) : null}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handleRefresh}
                      title="Refresh preview"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handlePrintPreview}
                      disabled={!previewUrl || isLoading}
                      title="Print preview on regular printer"
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setPreviewModalOpen(true)}
                      title="Fullscreen"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <span className="text-sm text-muted-foreground text-center px-4">
                  Add elements to see preview
                </span>
              )}
            </div>
          </TabsContent>

          <TabsContent value="code" className="flex-1 p-3 pt-2 flex flex-col min-h-0">
            <div className="flex-1 rounded-md border overflow-auto min-h-0">
              <pre className="text-xs font-mono p-3 whitespace-pre-wrap break-all">
                {zplCode}
              </pre>
            </div>
          </TabsContent>
        </Tabs>

        <div className="p-3 border-t flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" />
            {copied ? 'Copied!' : 'Copy'}
          </Button>
          <Button variant="outline" size="sm" className="flex-1" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-1" />
            Download
          </Button>
        </div>
      </div>

      {/* Labelary Preview Modal */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Label Preview (Labelary)</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-6 bg-muted rounded-md min-h-[400px]">
            {elements.length > 0 ? (
              previewError ? (
                <span className="text-sm text-destructive">{previewError}</span>
              ) : previewUrl ? (
                <div className="p-4 bg-muted">
                  <img
                    key={`modal-${imageKey}`}
                    src={previewUrl}
                    alt="Label Preview"
                    className="max-w-full max-h-[70vh] object-contain shadow-lg border border-gray-300"
                  />
                </div>
              ) : (
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              )
            ) : (
              <span className="text-sm text-muted-foreground">
                Add elements to see preview
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground text-center">
            Preview generated by Labelary API - {label.width}x{label.height}mm @ {label.dpi} DPI
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
